import * as React from 'react';
import { useAuth } from '@/hooks/use-auth';
import { getGroupAccessState, retryGroupAccess, subscribeGroupAccess } from '@/lib/group-access-recovery';
import { useSession } from './user-provider';
import { Button } from '@/components/ui/button';

export function GroupAccessRecovery({ unavailable = false, setup = false, onRetry }: {
  unavailable?: boolean; setup?: boolean; onRetry: () => void | Promise<unknown>;
}) {
  const { enabled, ready, requestGraphConsent, interactionError } = useAuth();
  const state = React.useSyncExternalStore(subscribeGroupAccess, getGroupAccessState, getGroupAccessState);
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const alive = React.useRef(true);
  React.useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  if (!enabled || (!unavailable && !setup && state !== 'consent' && state !== 'unavailable')) return null;
  const consent = setup || state === 'consent';
  return <div className="space-y-2 rounded-md border p-3 text-sm" aria-label="Microsoft group access recovery">
    <p role="status">{consent
      ? 'Microsoft group access needs approval. A Microsoft administrator must approve delegated group reading for the application you use to sign in.'
      : 'Microsoft group membership could not be verified. Retry when Microsoft is available. Existing individual access is preserved.'}</p>
    <p className="text-muted-foreground">After approval, retry group access to refresh reads. Submit any failed change again after reviewing it.</p>
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" disabled={!ready || busy} onClick={() => requestGraphConsent('groups')}>Connect Microsoft groups</Button>
      <Button variant="outline" disabled={!ready || busy} onClick={() => {
        setBusy(true); setFailed(false); retryGroupAccess();
        void Promise.resolve().then(onRetry).catch(() => { if (alive.current) setFailed(true); })
          .finally(() => { if (alive.current) setBusy(false); });
      }}>Retry group access</Button>
    </div>
    {failed ? <p role="alert">Group access is still unavailable. Try again or contact your Microsoft administrator.</p> : null}
    {interactionError ? <p role="alert">{interactionError}</p> : null}
  </div>;
}

/** The outer session observer owns discovery, including before organisation selection. */
export function CurrentUserGroupRecovery() {
  const { session, refreshUser } = useSession();
  const user = session.status === 'signedIn' ? session.user : null;
  if (user?.viewAsRole || (user && user.groupAccess?.state !== 'unavailable') ||
    (session.status === 'accessError' && !session.groupUnavailable)) return null;
  return <GroupAccessRecovery unavailable={user?.groupAccess?.state === 'unavailable' || (session.status === 'accessError' && session.groupUnavailable)}
    onRetry={() => refreshUser(true)} />;
}
